import type { ReactNode } from 'react';
import birdLogo from '../../assets/matou-bird-logo-grey.svg';
import textLogo from '../../assets/matou-text-logo-white.svg';

// Shared chrome for the standalone legal pages (Terms, Privacy). The home
// Navigation/Footer scroll to on-page anchors (#about, #contact, …) which do
// not exist off the home page, so these pages use their own slim header and
// footer instead — same brand tokens, no dead anchor links.

export function LegalLayout({
  title,
  updated,
  children,
}: {
  title: string;
  updated: string;
  children: ReactNode;
}) {
  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-matou-bg text-matou-text font-mono flex flex-col">
      {/* Header */}
      <header className="bg-matou-teal-dark">
        <div className="max-w-content mx-auto px-4 md:px-8">
          <div className="flex items-center h-16">
            <a href="/" className="flex items-center gap-3">
              <img src={birdLogo} alt="Mātou bird" className="h-6" />
              <img src={textLogo} alt="Mātou Collective" className="h-5" />
            </a>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <article className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-matou-teal-dark">
            {title}
          </h1>
          <p className="mt-3 text-xs uppercase tracking-widest text-matou-text-muted">
            Last updated {updated}
          </p>
          <div className="mt-8">{children}</div>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-matou-teal-dark border-t border-white/10">
        <div className="max-w-content mx-auto px-4 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            &copy; {currentYear} Mātou Collective. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="/"
              className="text-sm text-white/60 hover:text-matou-teal-soft transition-colors duration-150"
            >
              matou.nz
            </a>
            <a
              href="mailto:info@matou.nz"
              className="text-sm text-white/60 hover:text-matou-teal-soft transition-colors duration-150"
            >
              info@matou.nz
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Small typographic helpers so the pages read as content, not markup. No
// @tailwindcss/typography plugin is installed, so styles are applied directly.

export function Lead({ children }: { children: ReactNode }) {
  return (
    <p className="text-base leading-relaxed text-matou-text mb-6">{children}</p>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-xl font-bold text-matou-teal-deep mb-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function P({ children }: { children: ReactNode }) {
  return (
    <p className="text-[15px] leading-relaxed text-matou-text mb-4">{children}</p>
  );
}

export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="list-disc pl-5 space-y-2 mb-4 text-[15px] leading-relaxed text-matou-text">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}
